import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { authAPI } from '../services/api';
import { clearAllCarts } from '../utils/cartStorage';
import logger from '../utils/logger';
import DailyLoginModal from '../components/DailyLoginModal';
import ForcedLogoutModal from '../components/ForcedLogoutModal';

const AuthContext = createContext();

// Security Configuration
const SECURITY_CONFIG = {
  // Auto-logout after 30 minutes of inactivity (adjustable)
  INACTIVITY_TIMEOUT: 30 * 60 * 1000, // 30 minutes in milliseconds
  
  // Warning before logout (5 minutes before)
  WARNING_TIMEOUT: 5 * 60 * 1000, // 5 minutes in milliseconds
  
  // Check interval for activity (every 30 seconds)
  CHECK_INTERVAL: 30 * 1000, // 30 seconds
  
  // Events that count as user activity
  ACTIVITY_EVENTS: ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click']
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [showDailyLoginModal, setShowDailyLoginModal] = useState(false);
  const [dailyLoginData, setDailyLoginData] = useState(null);
  const [showForcedLogoutModal, setShowForcedLogoutModal] = useState(false);
  
  // Activity tracking refs
  const lastActivityTime = useRef(Date.now());
  const timeoutWarningTimer = useRef(null);
  const autoLogoutTimer = useRef(null);
  const activityCheckTimer = useRef(null);

  // Reset activity timer
  const resetActivity = useCallback(() => {
    lastActivityTime.current = Date.now();
    setShowTimeoutWarning(false);
    
    // Clear existing timers
    if (timeoutWarningTimer.current) {
      clearTimeout(timeoutWarningTimer.current);
    }
    if (autoLogoutTimer.current) {
      clearTimeout(autoLogoutTimer.current);
    }
    
    // Only set new timers if user is authenticated
    if (isAuthenticated) {
      // Set warning timer (25 minutes)
      timeoutWarningTimer.current = setTimeout(() => {
        setShowTimeoutWarning(true);
      }, SECURITY_CONFIG.INACTIVITY_TIMEOUT - SECURITY_CONFIG.WARNING_TIMEOUT);
      
      // Set auto-logout timer (30 minutes)
      autoLogoutTimer.current = setTimeout(() => {
        handleAutoLogout();
      }, SECURITY_CONFIG.INACTIVITY_TIMEOUT);
    }
  }, [isAuthenticated]);

  // Handle automatic logout
  const handleAutoLogout = useCallback(async () => {
    logger.info('🔒 Auto-logout: Session expired due to inactivity');
    
    // Clear all timers
    clearAllTimers();
    
    // Clear authentication data
    clearAuthData();
    
    // Show notification
    alert('⚠️ Security Notice: You have been automatically logged out due to inactivity.\n\nFor your security, please log in again to continue.');
    
    // Clear any sensitive data from localStorage/sessionStorage
    clearAllCarts();
    sessionStorage.clear();
    
    // Redirect to login page
    window.location.href = '/login';
  }, []);

  // Clear all security timers
  const clearAllTimers = useCallback(() => {
    if (timeoutWarningTimer.current) {
      clearTimeout(timeoutWarningTimer.current);
      timeoutWarningTimer.current = null;
    }
    if (autoLogoutTimer.current) {
      clearTimeout(autoLogoutTimer.current);
      autoLogoutTimer.current = null;
    }
    if (activityCheckTimer.current) {
      clearInterval(activityCheckTimer.current);
      activityCheckTimer.current = null;
    }
  }, []);

  // Activity event handler
  const handleActivity = useCallback(() => {
    if (isAuthenticated) {
      resetActivity();
    }
  }, [isAuthenticated, resetActivity]);

  // Setup activity monitoring
  useEffect(() => {
    if (isAuthenticated) {
      // Add activity event listeners
      SECURITY_CONFIG.ACTIVITY_EVENTS.forEach(event => {
        document.addEventListener(event, handleActivity, true);
      });
      
      // Start activity monitoring
      resetActivity();
      
      // Periodic check for activity (backup)
      activityCheckTimer.current = setInterval(() => {
        const timeSinceLastActivity = Date.now() - lastActivityTime.current;
        
        if (timeSinceLastActivity >= SECURITY_CONFIG.INACTIVITY_TIMEOUT) {
          handleAutoLogout();
        } else if (timeSinceLastActivity >= SECURITY_CONFIG.INACTIVITY_TIMEOUT - SECURITY_CONFIG.WARNING_TIMEOUT) {
          setShowTimeoutWarning(true);
        }
      }, SECURITY_CONFIG.CHECK_INTERVAL);
      
      return () => {
        // Cleanup event listeners
        SECURITY_CONFIG.ACTIVITY_EVENTS.forEach(event => {
          document.removeEventListener(event, handleActivity, true);
        });
        clearAllTimers();
      };
    } else {
      // Clear timers when not authenticated
      clearAllTimers();
      setShowTimeoutWarning(false);
    }
  }, [isAuthenticated, handleActivity, resetActivity, handleAutoLogout, clearAllTimers]);

  // Check if user is logged in on app start
  useEffect(() => {
    const handleForcedLogout = () => {
      setShowForcedLogoutModal(true);
    };

    window.addEventListener('forced-logout', handleForcedLogout);

    checkAuthStatus();
    return () => {
      window.removeEventListener('forced-logout', handleForcedLogout);
    };
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setLoading(false);
        return;
      }

      // Verify token by fetching user profile
      const response = await authAPI.getProfile();
      if (response.success) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        
        // Initialize activity tracking
        lastActivityTime.current = Date.now();
        
        // Check daily login status on app load
        try {
          const dailyLoginResponse = await authAPI.getDailyLoginStatus();
          if (dailyLoginResponse.success && dailyLoginResponse.data) {
            const dailyLogin = dailyLoginResponse.data;
            // Only show modal if it's a new login or reward can be claimed
            if (dailyLogin.isNewLogin || dailyLogin.canClaimReward) {
              setDailyLoginData(dailyLogin);
              setShowDailyLoginModal(true);
            }
          }
        } catch (error) {
          // Silently fail - daily login is not critical
          logger.debug('Daily login check failed:', error);
        }
      } else {
        // Token is invalid, clear it
        clearAuthData();
      }
    } catch (error) {
      logger.error('Auth check failed:', error);
      clearAuthData();
    } finally {
      setLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      
      if (response.success) {
        const { user, accessToken, refreshToken, dailyLogin } = response.data;
        if (accessToken) localStorage.setItem('accessToken', accessToken);
        if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
        setUser(user);
        setIsAuthenticated(true);
        
        // Initialize activity tracking
        lastActivityTime.current = Date.now();
        
        // Show daily login modal if it's a new login or reward can be claimed (not for admins)
        if (dailyLogin && user.role !== 'admin' && (dailyLogin.isNewLogin || dailyLogin.canClaimReward)) {
          setDailyLoginData(dailyLogin);
          setShowDailyLoginModal(true);
        }
        
        logger.debug('🔐 User logged in successfully with auto-logout security enabled');
        
        return { success: true, user };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      logger.error('Login error:', error);
      return { 
        success: false, 
        message: error.message || 'Login failed' 
      };
    }
  };

  const adminLogin = async (credentials) => {
    try {
      const response = await authAPI.adminLogin(credentials);
      
      if (response.success) {
        const { user, accessToken, refreshToken, dailyLogin } = response.data;
        if (accessToken) localStorage.setItem('accessToken', accessToken);
        if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
        setUser(user);
        setIsAuthenticated(true);
        
        // Initialize activity tracking
        lastActivityTime.current = Date.now();
        
        // Show daily login modal if it's a new login or reward can be claimed (admins can also participate)
        if (dailyLogin && (dailyLogin.isNewLogin || dailyLogin.canClaimReward)) {
          setDailyLoginData(dailyLogin);
          setShowDailyLoginModal(true);
        }
        
        logger.debug('🔐 Admin logged in successfully with auto-logout security enabled');
        
        return { success: true, user };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      logger.error('Admin login error:', error);
      return { 
        success: false, 
        message: error.message || 'Admin login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      
      if (response.success) {
        const { user, accessToken, refreshToken } = response.data;
        if (accessToken) localStorage.setItem('accessToken', accessToken);
        if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
        setUser(user);
        setIsAuthenticated(true);
        
        // Initialize activity tracking
        lastActivityTime.current = Date.now();
        
        logger.debug('🔐 User registered successfully with auto-logout security enabled');
        
        return { success: true, user };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      // Improved error logging
      const errorMessage = error?.message || error?.response?.data?.message || 'Registration failed';
      const errorDetails = {
        message: errorMessage,
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        code: error?.code || error?.response?.data?.code,
        validationErrors: error?.response?.data?.errors || error?.validationErrors
      };
      
      logger.error('Registration error:', errorMessage);
      logger.error('Registration error details:', JSON.stringify(errorDetails, null, 2));
      
      // The API interceptor unwraps error.response.data, so error is already the response data
      // Error structure: { success: false, code, message, requestId, details: { errors: [...] } }
      const errorData = error;
      const details = errorData.details;
      
      // Extract validation errors if present in details.errors
      if (details?.errors && Array.isArray(details.errors)) {
        const validationErrors = {};
        details.errors.forEach(err => {
          // Extract field name from path (e.g., "body.firstName" -> "firstName")
          const fieldName = err.path?.replace('body.', '') || '';
          if (fieldName) {
            validationErrors[fieldName] = err.message;
          }
        });
        
        return {
          success: false,
          message: errorData.message || 'Please fix the validation errors below',
          validationErrors
        };
      }
      
      // Also check if errors are directly in errorData (fallback)
      if (errorData.errors && Array.isArray(errorData.errors)) {
        const validationErrors = {};
        errorData.errors.forEach(err => {
          // Handle both string errors and object errors
          if (typeof err === 'string') {
            // If it's a string, try to extract field name from common patterns
            if (err.includes('firstName')) {
              validationErrors.firstName = err;
            } else if (err.includes('lastName')) {
              validationErrors.lastName = err;
            } else if (err.includes('email')) {
              validationErrors.email = err;
            } else if (err.includes('password')) {
              validationErrors.password = err;
            }
          } else if (err.path) {
            // Extract field name from path (e.g., "body.firstName" -> "firstName")
            const fieldName = err.path.replace('body.', '');
            validationErrors[fieldName] = err.message;
          }
        });
        
        return {
          success: false,
          message: errorData.message || 'Please fix the validation errors below',
          validationErrors
        };
      }
      
      return { 
        success: false, 
        message: errorData.message || error.message || 'Registration failed',
        code: errorData.code || error.code
      };
    }
  };

  const logout = async (isAutoLogout = false) => {
    try {
      // Clear all security timers
      clearAllTimers();
      
      // Call logout endpoint to invalidate refresh token
      if (!isAutoLogout) {
        await authAPI.logout();
        logger.debug('🔓 User logged out manually');
      }
    } catch (error) {
      logger.error('Logout API error:', error);
      // Continue with local logout even if API call fails
    } finally {
      clearAuthData();
    }
  };

  const clearAuthData = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setIsAuthenticated(false);
    setShowTimeoutWarning(false);
  };

  const refreshUserData = useCallback(async () => {
    try {
      const response = await authAPI.getProfile();
      
      if (response.success) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        return { success: true, user: response.data.user };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      logger.error('Refresh user data error:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to refresh user data' 
      };
    }
  }, []); // Empty dependency array - function doesn't depend on any props or state

  const updateProfile = async (profileData) => {
    try {
      const response = await authAPI.updateProfile(profileData);
      
      if (response.success) {
        setUser(response.data.user);
        
        // Reset activity on profile update
        resetActivity();
        
        return { success: true, user: response.data.user };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      logger.error('Profile update error:', error);
      return { 
        success: false, 
        message: error.message || 'Profile update failed' 
      };
    }
  };

  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await authAPI.refreshToken(refreshToken);
      
      if (response.success) {
        localStorage.setItem('accessToken', response.data.accessToken);
        
        // Reset activity on token refresh
        resetActivity();
        
        return response.data.accessToken;
      } else {
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      logger.error('Token refresh error:', error);
      clearAuthData();
      throw error;
    }
  };

  // Extend session (when user dismisses warning)
  const extendSession = useCallback(() => {
    resetActivity();
    logger.debug('🔄 Session extended by user action');
  }, [resetActivity]);

  // Helper functions
  const isAdmin = () => {
    return user && user.role === 'admin';
  };

  const isCustomer = () => {
    return user && user.role === 'customer';
  };

  const isSuperAdmin = () => {
    return user && user.email === 'admin@batinosgarden.com';
  };

  // Get remaining time before auto-logout
  const getTimeUntilLogout = useCallback(() => {
    if (!isAuthenticated) return 0;
    const timeSinceLastActivity = Date.now() - lastActivityTime.current;
    return Math.max(0, SECURITY_CONFIG.INACTIVITY_TIMEOUT - timeSinceLastActivity);
  }, [isAuthenticated]);

  // Get token from localStorage
  const getToken = () => {
    return localStorage.getItem('accessToken');
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    showTimeoutWarning,
    token: getToken(), // Expose token for Socket.io
    login,
    adminLogin,
    register,
    logout,
    updateProfile,
    refreshUserData,
    refreshAccessToken,
    isAdmin,
    isCustomer,
    isSuperAdmin,
    checkAuthStatus,
    extendSession,
    getTimeUntilLogout,
    resetActivity
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <DailyLoginModal
        isOpen={showDailyLoginModal}
        onClose={() => {
          setShowDailyLoginModal(false);
          setDailyLoginData(null);
        }}
        dailyLoginData={dailyLoginData}
        onRefreshDailyLogin={async () => {
          try {
            const dailyLoginResponse = await authAPI.getDailyLoginStatus();
            if (dailyLoginResponse.success && dailyLoginResponse.data) {
              setDailyLoginData(dailyLoginResponse.data);
            }
          } catch (error) {
            logger.debug('Failed to refresh daily login status:', error);
          }
        }}
      />
      <ForcedLogoutModal
        isOpen={showForcedLogoutModal}
        onClose={() => setShowForcedLogoutModal(false)}
      />
    </AuthContext.Provider>
  );
}; 